import { Images } from "#/themes";
import { Theme } from "#/types.ts/other";

/** Filename the browser saves the archive as. */
export const BRAND_ASSET_ARCHIVE_NAME = "stream-brand-assets.zip";

/** Route that builds and serves the archive. */
export const BRAND_ASSET_DOWNLOAD_PATH = "/api/brand-assets";

/**
 * The kit is Stream's *own* marks, so it only belongs on Stream-branded shells.
 * White-label partners run this same unauthenticated shell under their own logo
 * and colours; handing them a Stream logo ZIP next to their branding is brand
 * confusion before it's anything else.
 *
 * Gated on `site` — the route segment `withTheme` resolves the theme from, and
 * the only value that yields `StreamTheme` — rather than on the display `name`
 * a partner could happen to have "stream" inside of.
 */
export const isStreamBrandedSite = (theme?: Pick<Theme, "site">): boolean =>
  theme?.site?.toLowerCase() === "stream";

export type BrandAsset = {
  /** CDN source. Always hardcoded here — never derived from request input. */
  url: string;
  /** Name this file gets inside the archive. */
  filename: string;
  /** Usage note, also written into the archive's README. */
  usage: string;
};

/**
 * The Stream brand marks we let people download.
 *
 * The naming here is deliberate, because the keys in `themes/images.ts` read
 * backwards from what you'd assume: `logoDark` is the *dark ink* wordmark
 * (mean opaque luminance ~49), so it belongs on light backgrounds, while
 * `logo` is the white wordmark (~225) — the one on our own dark nav bar.
 * Filenames and `usage` describe the artwork, not the key, so nobody
 * downloading this has to guess.
 */
export const BRAND_ASSETS: BrandAsset[] = [
  {
    url: Images.logoDark,
    filename: "stream-logo-dark.png",
    usage: "Full logo in dark ink — use on light backgrounds.",
  },
  {
    url: Images.logo,
    filename: "stream-logo-light.png",
    usage: "Full logo in white — use on dark backgrounds.",
  },
  {
    url: Images.icon,
    filename: "stream-icon.png",
    usage: "Icon mark on its own — use where the full logo won't fit.",
  },
];

/** Public terms the archive's usage note defers to. */
export const BRAND_TERMS_URL = "https://www.streamorders.com/terms";

/**
 * Plain-text guide shipped inside the archive so the filenames aren't ambiguous
 * — and so the files don't travel without their terms.
 *
 * The archive leaves our servers and gets forwarded, re-zipped, and dropped in
 * shared drives; the README is the only thing that stays attached to it. It
 * states permitted and prohibited use and points at the published terms rather
 * than restating them, so this file can't drift out of sync with what legal
 * actually publishes.
 */
export const buildBrandAssetReadme = (
  assets: BrandAsset[] = BRAND_ASSETS,
): string =>
  [
    "Stream brand assets",
    "===================",
    "",
    ...assets.map((asset) => `${asset.filename}\n    ${asset.usage}`),
    "",
    "All files are transparent PNGs.",
    "",
    "Using these marks",
    "-----------------",
    "",
    "Permitted:",
    "  - Co-marketing and partner materials",
    "  - Press and media coverage",
    "  - Integration and documentation pages that reference Stream",
    "",
    "Not permitted:",
    "  - Implying endorsement, partnership, or certification you don't have",
    "  - Altering the marks — don't recolour, rotate, stretch, or crop them",
    "  - Use outside an authorised Stream relationship",
    "",
    `Use of these marks is subject to the Stream terms at ${BRAND_TERMS_URL}.`,
    "Questions about a specific use? Ask your Stream contact first.",
    "",
  ].join("\n");
